/**
 * DixonColesMobileCard - Mobile-optimized fixture card
 */

import React, { useState } from 'react';
import { useDCTeamStats } from '../../swr-config';
import { MatchAnalysis } from '../match-analysis';
import type { DixonColesFixture, TeamRollupOut } from '../../types';

interface Props {
  fixture: DixonColesFixture;
  isExpanded: boolean;
  onToggle: () => void;
}

const MARKET_LABELS: Record<string, string> = {
  home_win: 'Home',
  draw: 'Draw',
  away_win: 'Away',
  over_1_5: 'O1.5',
  over_2_5: 'O2.5',
  over_3_5: 'O3.5',
  btts_yes: 'BTTS',
};

// Format kickoff time
function formatKickoff(utcString: string): string {
  const date = new Date(utcString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();

  const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (isToday) return `Today ${timeStr}`;
  if (isTomorrow) return `Tomorrow ${timeStr}`;

  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DixonColesMobileCard({ fixture, isExpanded, onToggle }: Props) {
  const probs = fixture.probabilities;
  const odds = fixture.odds;
  const edges = fixture.value_edges;

  const formatProb = (p: number | undefined | null) => {
    if (p === undefined || p === null) return '-';
    return `${(p * 100).toFixed(0)}%`;
  };

  const formatOdds = (o: number | undefined | null) => {
    if (o === undefined || o === null) return '-';
    return o.toFixed(2);
  };

  // Get edge class
  const getEdgeClass = (edge: number | null | undefined) => {
    if (edge === undefined || edge === null) return '';
    if (edge >= 5) return 'edge-high';
    if (edge >= 2) return 'edge-mid';
    if (edge > 0) return 'edge-low';
    return 'edge-negative';
  };

  return (
    <div className={`dc-mobile-card ${isExpanded ? 'expanded' : ''} ${!fixture.has_dc_data ? 'no-dc-data' : ''}`}>
      {/* Header */}
      <div className="card-header" onClick={onToggle}>
        <div className="match-info">
          <div className="kickoff">{formatKickoff(fixture.kickoff_utc)}</div>
          <div className="league">
            {fixture.league.logo && (
              <img src={fixture.league.logo} alt="" className="league-logo" />
            )}
            <span>{fixture.league.name}</span>
          </div>
        </div>

        {/* Best edge badge */}
        {edges?.best_edge_value !== undefined && edges.best_edge_value !== null && (
          <div className={`edge-badge ${getEdgeClass(edges.best_edge_value)}`}>
            {edges.best_edge_value > 0 ? '+' : ''}{edges.best_edge_value.toFixed(1)}%
            <span className="edge-market">
              {MARKET_LABELS[edges.best_edge_market || ''] || edges.best_edge_market}
            </span>
          </div>
        )}
      </div>

      {/* Teams */}
      <div className="card-teams" onClick={onToggle}>
        <div className="team home">
          {fixture.home_team.logo && (
            <img src={fixture.home_team.logo} alt="" className="team-logo" />
          )}
          <span className="team-name">{fixture.home_team.name}</span>
        </div>
        <div className="vs">vs</div>
        <div className="team away">
          <span className="team-name">{fixture.away_team.name}</span>
          {fixture.away_team.logo && (
            <img src={fixture.away_team.logo} alt="" className="team-logo" />
          )}
        </div>
      </div>

      {/* Expected Goals */}
      {probs?.expected_home_goals != null && probs?.expected_away_goals != null && (
        <div className="card-xg" onClick={onToggle}>
          <span className="xg-label">xG:</span>
          <span className="xg-value">
            {probs.expected_home_goals.toFixed(1)} - {probs.expected_away_goals.toFixed(1)}
          </span>
        </div>
      )}

      {/* Key Markets Grid */}
      <div className="card-markets">
        {/* 1X2 */}
        <div className="market-group">
          <div className="market-header">1X2</div>
          <div className="market-row">
            <MarketCell label="H" prob={probs?.home_win} odds={odds.home_win} />
            <MarketCell label="D" prob={probs?.draw} odds={odds.draw} />
            <MarketCell label="A" prob={probs?.away_win} odds={odds.away_win} />
          </div>
        </div>

        {/* Goals */}
        <div className="market-group">
          <div className="market-header">Goals</div>
          <div className="market-row">
            <MarketCell label="O2.5" prob={probs?.over_2_5} odds={odds.over_2_5} />
            <MarketCell label="BTTS" prob={probs?.btts_yes} odds={odds.btts_yes} />
          </div>
        </div>
      </div>

      {/* Expand toggle */}
      <button className="card-expand-btn" onClick={onToggle} type="button">
        <span>{isExpanded ? 'Hide Stats' : 'Show Stats'}</span>
        <svg
          className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {/* Expanded Stats */}
      {isExpanded && (
        <div className="card-expanded">
          <MatchAnalysis
            fixtureId={fixture.fixture_id}
            homeTeam={{
              id: fixture.home_team.id,
              name: fixture.home_team.name,
              crest_url: fixture.home_team.logo,
            }}
            awayTeam={{
              id: fixture.away_team.id,
              name: fixture.away_team.name,
              crest_url: fixture.away_team.logo,
            }}
            leagueId={fixture.league.id}
            leagueName={fixture.league.name}
            leagueLogo={fixture.league.logo}
          />
        </div>
      )}
    </div>
  );
}

function MarketCell({
  label,
  prob,
  odds,
}: {
  label: string;
  prob?: number | null;
  odds?: number | null;
}) {
  return (
    <div className="market-cell">
      <span className="market-label">{label}</span>
      <span className="market-prob">
        {prob !== undefined && prob !== null ? `${(prob * 100).toFixed(0)}%` : '-'}
      </span>
      <span className="market-odds">
        {odds !== undefined && odds !== null ? odds.toFixed(2) : '-'}
      </span>
    </div>
  );
}

type WindowKey = 'season' | 'last10' | 'last5' | 'last3';

// Stats matching desktop expanded row
const OVERALL_STATS = [
  { key: 'avg_goals_for', label: 'Goals' },
  { key: 'avg_goals_against', label: 'Conceded' },
  { key: 'avg_xg', label: 'xG' },
  { key: 'avg_xg_against', label: 'xG Against' },
  { key: 'avg_corners', label: 'Corners' },
  { key: 'avg_corners_against', label: 'Corners Ag.' },
  { key: 'avg_shots', label: 'Shots' },
  { key: 'avg_shots_on_target', label: 'SOT' },
  { key: 'avg_saves', label: 'Saves' },
  { key: 'avg_tackles', label: 'Tackles' },
  { key: 'total_cards', label: 'Cards', perMatch: true, computed: true },
  { key: 'offsides', label: 'Offsides', perMatch: true },
  { key: 'avg_fouls', label: 'Fouls' },
  { key: 'avg_fouls_against', label: 'Fouls Ag.' },
  { key: 'avg_possession', label: 'Poss %' },
  { key: 'pass_accuracy_pct', label: 'Pass Acc %', format: 'pct_raw' as const },
  { key: 'btts_rate', label: 'BTTS %', format: 'pct' as const },
  { key: 'clean_sheet_rate', label: 'CS %', computed: true, format: 'pct_raw' as const },
] as const;

const VENUE_STATS = [
  { key: 'avg_goals_for', homeKey: 'avg_goals_for_home', awayKey: 'avg_goals_for_away', label: 'Goals (H/A)' },
  { key: 'avg_goals_against', homeKey: 'avg_goals_against_home', awayKey: 'avg_goals_against_away', label: 'Conceded (H/A)' },
] as const;

function TrendArrow({ current, season }: { current: number | null; season: number | null }) {
  if (current === null || season === null) return null;
  const diff = current - season;
  const threshold = 0.01;
  if (Math.abs(diff) < threshold) {
    return <span className="trend-arrow neutral">—</span>;
  } else if (diff > 0) {
    return <span className="trend-arrow up">▲</span>;
  } else {
    return <span className="trend-arrow down">▼</span>;
  }
}

function MobileExpandedStats({ fixtureId }: { fixtureId: number }) {
  const [window, setWindow] = useState<WindowKey>('season');
  const { data, isLoading, error } = useDCTeamStats(fixtureId);

  if (isLoading) {
    return (
      <div className="mobile-stats-loading">
        <div className="spinner" />
        Loading stats...
      </div>
    );
  }

  if (error || !data || data.length < 2) {
    return <div className="mobile-stats-error">Unable to load team stats</div>;
  }

  const [homeTeam, awayTeam] = data;
  const homeData = homeTeam.windows[window] || {};
  const awayData = awayTeam.windows[window] || {};
  const homeSeasonData = homeTeam.windows.season || {};
  const awaySeasonData = awayTeam.windows.season || {};
  const homeMatches = (homeData as any).matches || 0;
  const awayMatches = (awayData as any).matches || 0;
  const homeSeasonMatches = (homeSeasonData as any).matches || 0;
  const awaySeasonMatches = (awaySeasonData as any).matches || 0;
  const showTrend = window !== 'season';

  const getStatValue = (
    sourceData: Record<string, any>,
    key: string,
    matchCount: number,
    perMatch?: boolean
  ) => {
    if (key === 'total_cards') {
      const yellow = (sourceData.cards_y as number) || 0;
      const red = (sourceData.cards_r as number) || 0;
      const total = yellow + red;
      if (perMatch && matchCount > 0) {
        return total / matchCount;
      }
      return total;
    }
    if (key === 'clean_sheet_rate') {
      const cleanSheets = (sourceData.clean_sheets as number) || 0;
      if (matchCount > 0) {
        return (cleanSheets / matchCount) * 100;
      }
      return null;
    }
    const val = sourceData[key];
    if (perMatch && matchCount > 0 && typeof val === 'number') {
      return val / matchCount;
    }
    return typeof val === 'number' ? val : null;
  };

  const formatVal = (val: number | null | undefined, format?: string) => {
    if (val === null || val === undefined) return '—';
    if (format === 'pct') return `${(val * 100).toFixed(0)}%`;
    if (format === 'pct_raw') return `${val.toFixed(0)}%`;
    return val.toFixed(1);
  };

  return (
    <div className="mobile-expanded-stats">
      {/* Window toggle */}
      <div className="mobile-stats-windows">
        {(['season', 'last10', 'last5', 'last3'] as const).map(w => (
          <button
            key={w}
            className={`chip ${window === w ? 'active' : ''}`}
            onClick={() => setWindow(w)}
            type="button"
          >
            {w === 'season' ? 'Season' : w.replace('last', 'L')}
          </button>
        ))}
      </div>

      {/* Team header */}
      <div className="mobile-stats-team-header">
        <div className="team-col home">
          {homeTeam.team.crest_url && (
            <img src={homeTeam.team.crest_url} alt="" className="team-crest" />
          )}
          <div className="team-info">
            <span className="team-name">{homeTeam.team.name}</span>
            <span className="team-matches">{homeMatches} matches</span>
          </div>
        </div>
        <div className="vs-col">vs</div>
        <div className="team-col away">
          <div className="team-info">
            <span className="team-name">{awayTeam.team.name}</span>
            <span className="team-matches">{awayMatches} matches</span>
          </div>
          {awayTeam.team.crest_url && (
            <img src={awayTeam.team.crest_url} alt="" className="team-crest" />
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="mobile-stats-grid">
        {OVERALL_STATS.map((row) => {
          const homeVal = getStatValue(homeData as any, row.key, homeMatches, 'perMatch' in row ? row.perMatch : undefined);
          const awayVal = getStatValue(awayData as any, row.key, awayMatches, 'perMatch' in row ? row.perMatch : undefined);
          const homeSeasonVal = getStatValue(homeSeasonData as any, row.key, homeSeasonMatches, 'perMatch' in row ? row.perMatch : undefined);
          const awaySeasonVal = getStatValue(awaySeasonData as any, row.key, awaySeasonMatches, 'perMatch' in row ? row.perMatch : undefined);
          const format = 'format' in row ? row.format : undefined;

          return (
            <div key={row.key} className="mobile-stat-row">
              <span className="stat-value home">
                {formatVal(homeVal, format)}
                {showTrend && <TrendArrow current={homeVal} season={homeSeasonVal} />}
              </span>
              <span className="stat-label">{row.label}</span>
              <span className="stat-value away">
                {showTrend && <TrendArrow current={awayVal} season={awaySeasonVal} />}
                {formatVal(awayVal, format)}
              </span>
            </div>
          );
        })}

        {/* Venue-specific stats */}
        <div className="mobile-stats-divider" />
        {VENUE_STATS.map((row) => {
          const homeVal = (homeData as any)[row.homeKey] as number | null;
          const awayVal = (awayData as any)[row.awayKey] as number | null;

          return (
            <div key={row.key} className="mobile-stat-row venue">
              <span className="stat-value home">{formatVal(homeVal)}</span>
              <span className="stat-label">{row.label}</span>
              <span className="stat-value away">{formatVal(awayVal)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
